import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { IPerioChart } from '@dataTypes/chartingFeature.types'
import {
  ActionButton,
  ActionButtons,
  DataTable,
  EmptyState,
  type DataTableColumn,
  type DataTableRow,
  type DataTableSortDirection,
} from '@enamelai/design-system'
import { Icon as NotesIcon } from '@enamelai/design-system/icons/Notes'
import ChartingDeleteItemModal from '@components/pages/Charting/Modals/ChartingDeleteItemModal.component'
import ChartingDiscardInProgressModal from '@components/pages/Charting/Modals/ChartingDiscardInProgressModal.component'
import ChartingEditChartModal from '@components/pages/Charting/Modals/ChartingEditChartModal.component'
import { GridComponent, RowSelectEventArgs } from '@syncfusion/ej2-react-grids'
import { formatProviderDisplayName } from '@utils/provider-display.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { deletePerioChart } from '@store/actions'
import {
  setCreatePerioChart,
  setPerioUpdated,
  setPreferCreatePerioChart,
  setSelectedPerioChart,
} from '@store/reducers/charting.reducer'
import { getAlignedPerioEditChartId } from '@utils/active-perio-chart.utils'
import {
  compareChartHistorySortValues,
  handleChartHistoryTableSort,
} from '@utils/charting-history-table-sort.util'

interface PerioHistoryProps {
  onViewNote: (id: string, openFirstNote?: boolean) => void
  onSelectHistory: (chart: RowSelectEventArgs) => void
  gridRef: React.RefObject<GridComponent>
}

type PerioHistorySortColumn = 'date' | 'title' | 'provider_name'

type PerioHistoryRow = DataTableRow & {
  id: string
  date: string
  dateSortKey: number
  title: string
  provider_name: string
}

const PAGE_SIZE_OPTIONS = [5, 10, 15, 20]

const emitHistorySelection = (
  onSelectHistory: (chart: RowSelectEventArgs) => void,
  chartId: string | null,
): void => {
  onSelectHistory({
    name: chartId ? 'rowSelected' : 'rowDeselected',
    data: chartId ? { chart_id: chartId } : undefined,
  } as RowSelectEventArgs)
}

const PerioHistory: React.FC<PerioHistoryProps> = ({
  onViewNote,
  onSelectHistory,
}) => {
  const dispatch = useAppDispatch()
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const perioCharts = useAppSelector((state) => state.charting.perioCharts)
  const chartingLoading = useAppSelector((state) => state.charting.loading)
  const isPerioUpdated = useAppSelector(
    (state) => state.charting.isPerioUpdated,
  )
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const preferCreatePerioChart = useAppSelector(
    (state) => state.charting.preferCreatePerioChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const onViewNoteRef = useRef(onViewNote)
  onViewNoteRef.current = onViewNote
  const editingChartId = useMemo(
    (): string =>
      getAlignedPerioEditChartId(
        selectedPerioChart,
        createPerioChart,
        preferCreatePerioChart,
      ),
    [createPerioChart, preferCreatePerioChart, selectedPerioChart],
  )
  const editingChartIdRef = useRef(editingChartId)
  editingChartIdRef.current = editingChartId

  const [showEditConfirm, setShowEditConfirm] = useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [chartToEdit, setChartToEdit] = useState<IPerioChart | null>(null)
  const [chartToDelete, setChartToDelete] = useState<IPerioChart | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0])
  const [sortColumnId, setSortColumnId] =
    useState<PerioHistorySortColumn | null>(null)
  const [sortDirection, setSortDirection] =
    useState<DataTableSortDirection>('asc')

  const historyRows = useMemo(
    (): PerioHistoryRow[] =>
      selectedPatient && !chartingLoading
        ? perioCharts
            .filter((chart) => chart.patient_id === selectedPatient.patient_id)
            .map((chart) => ({
              id: chart.chart_id,
              date: new Date(
                chart.date_time_created_at ?? '',
              ).toLocaleDateString(),
              dateSortKey:
                new Date(chart.date_time_created_at ?? '').getTime() || 0,
              title: chart.title ?? '',
              provider_name: formatProviderDisplayName(chart.provider),
            }))
        : [],
    [selectedPatient, chartingLoading, perioCharts],
  )

  useEffect(() => {
    setCurrentPage(1)
    setSortColumnId(null)
    setSortDirection('asc')
  }, [selectedPatient?.patient_id, pageSize, historyRows.length])

  const getPerioSortValue = useCallback(
    (
      row: PerioHistoryRow,
      columnId: PerioHistorySortColumn,
    ): string | number => {
      switch (columnId) {
        case 'date':
          return row.dateSortKey
        case 'title':
          return row.title
        case 'provider_name':
          return row.provider_name
        default:
          return row.title
      }
    },
    [],
  )

  const sortedHistoryRows = useMemo((): PerioHistoryRow[] => {
    if (!sortColumnId) {
      return historyRows
    }
    const sorted = [...historyRows]
    sorted.sort((rowA, rowB) =>
      compareChartHistorySortValues(
        getPerioSortValue(rowA, sortColumnId),
        getPerioSortValue(rowB, sortColumnId),
        sortDirection,
      ),
    )
    return sorted
  }, [getPerioSortValue, historyRows, sortColumnId, sortDirection])

  const totalPages = Math.max(1, Math.ceil(sortedHistoryRows.length / pageSize))
  const pagedRows = useMemo((): PerioHistoryRow[] => {
    const start = (currentPage - 1) * pageSize
    return sortedHistoryRows.slice(start, start + pageSize)
  }, [currentPage, sortedHistoryRows, pageSize])

  useEffect(() => {
    const selectedId = selectedPerioChart?.chart_id?.trim()
    if (!selectedId) return
    const selectedIndex = sortedHistoryRows.findIndex(
      (row) => row.id === selectedId,
    )
    if (selectedIndex < 0) return
    setCurrentPage(Math.floor(selectedIndex / pageSize) + 1)
  }, [pageSize, selectedPerioChart?.chart_id, sortedHistoryRows])

  const handleSort = useCallback(
    (columnId: string): void => {
      handleChartHistoryTableSort(
        columnId,
        sortColumnId,
        sortDirection,
        (value) => setSortColumnId(value as PerioHistorySortColumn | null),
        setSortDirection,
        setCurrentPage,
      )
    },
    [sortColumnId, sortDirection],
  )

  const chartById = useCallback(
    (chartId: string): IPerioChart | undefined =>
      perioCharts.find((chart) => chart.chart_id === chartId),
    [perioCharts],
  )

  useEffect(() => {
    if (!preferCreatePerioChart) {
      return
    }
    setShowEditConfirm(false)
    setChartToEdit(null)
  }, [preferCreatePerioChart])

  const confirmEditChart = useCallback(
    (chart: IPerioChart): void => {
      dispatch(setCreatePerioChart({ ...chart }))
      dispatch(setSelectedPerioChart({ ...chart }))
      dispatch(setPreferCreatePerioChart(false))
      dispatch(setPerioUpdated(false))
      setShowEditConfirm(false)
    },
    [dispatch],
  )

  const handleDeleteChart = useCallback((chart: IPerioChart): void => {
    setShowDeleteConfirm(true)
    setChartToDelete(chart)
  }, [])

  const handleEditChart = useCallback((chart: IPerioChart): void => {
    setShowEditConfirm(true)
    setChartToEdit({ ...chart })
  }, [])

  const handleTableClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>): void => {
      const target = event.target as HTMLElement
      if (target.closest('button, a, input, label, [role="menu"]')) {
        return
      }
      const chartId = target
        .closest('tr.ds-data-table__row')
        ?.querySelector<HTMLElement>('[data-chart-history-id]')
        ?.getAttribute('data-chart-history-id')
        ?.trim()
      if (!chartId) {
        return
      }
      const isSelected =
        chartId === (selectedPerioChart?.chart_id?.trim() ?? '')
      emitHistorySelection(onSelectHistory, isSelected ? null : chartId)
    },
    [onSelectHistory, selectedPerioChart?.chart_id],
  )

  const columns = useMemo(
    (): DataTableColumn<PerioHistoryRow>[] => [
      {
        id: 'date',
        label: 'Date',
        sortable: true,
        renderCell: (row): React.ReactNode => (
          <span data-chart-history-id={row.id}>{row.date}</span>
        ),
      },
      {
        id: 'title',
        label: 'Chart name',
        sortable: true,
      },
      {
        id: 'provider_name',
        label: 'Provider',
        sortable: true,
      },
      {
        id: 'notes',
        label: 'Notes',
        align: 'center',
        width: 72,
        renderCell: (row): React.ReactNode => {
          const chart = chartById(row.id)
          if (!chart?.notes?.length) {
            return <span>-</span>
          }
          return (
            <div
              className="charting-history-notes-action"
              onClick={(event): void => event.stopPropagation()}
            >
              <ActionButton
                icon={
                  <NotesIcon
                    className="ds-action-button__svg"
                    width={16}
                    height={16}
                    aria-hidden
                  />
                }
                label="View notes"
                size="sm"
                tone="primary"
                variant="outline"
                onClick={(): void => {
                  onViewNoteRef.current(row.id, true)
                }}
              />
            </div>
          )
        },
      },
    ],
    [chartById],
  )

  const selectedRowIds = useMemo((): string[] => {
    const selectedId = selectedPerioChart?.chart_id?.trim() ?? ''
    return selectedId ? [selectedId] : []
  }, [selectedPerioChart?.chart_id])

  const showEmptyState = !chartingLoading && historyRows.length === 0

  return (
    <>
      <div className="perio-history">
        {!showEmptyState && (
          <div className="perio-history-title mb-1">Perio history</div>
        )}
        <div className="history-grid-container" onClick={handleTableClick}>
          {showEmptyState ? (
            <EmptyState
              className="charting-history-empty"
              title="No perio charts found"
              description={
                selectedPatient
                  ? "You don't have any perio charts on record yet."
                  : 'Select a patient to view their charts.'
              }
            />
          ) : (
            <DataTable<PerioHistoryRow>
              className="perio-history-table"
              columns={columns}
              rows={pagedRows}
              state={chartingLoading ? 'loading' : 'default'}
              sortColumnId={sortColumnId ?? undefined}
              sortDirection={sortColumnId ? sortDirection : undefined}
              onSort={handleSort}
              selectedRowIds={selectedRowIds}
              actionsLabel="Actions"
              renderActions={(row): React.ReactNode => {
                const chart = chartById(row.id)
                if (!chart) {
                  return null
                }
                return (
                  <div onClick={(event): void => event.stopPropagation()}>
                    <ActionButtons
                      size="sm"
                      showView={false}
                      disableEdit={row.id === editingChartIdRef.current}
                      onEdit={(): void => {
                        handleEditChart(chart)
                      }}
                      onDelete={(): void => {
                        handleDeleteChart(chart)
                      }}
                    />
                  </div>
                )
              }}
              pagination={{
                currentPage,
                totalPages,
                pageSize,
                pageSizeOptions: PAGE_SIZE_OPTIONS,
                onPageChange: setCurrentPage,
                onPageSizeChange: (nextSize): void => {
                  setPageSize(nextSize)
                  setCurrentPage(1)
                },
              }}
            />
          )}
        </div>
      </div>

      <ChartingDeleteItemModal
        show={showDeleteConfirm}
        itemName={chartToDelete?.title ?? ''}
        onCancel={(): void => setShowDeleteConfirm(false)}
        onDelete={(): void => {
          dispatch(deletePerioChart(chartToDelete?.chart_id ?? ''))
          setShowDeleteConfirm(false)
        }}
      />
      {isPerioUpdated && chartToEdit ? (
        <ChartingDiscardInProgressModal
          show={showEditConfirm}
          chartName={chartToEdit.title ?? ''}
          variant="perio"
          onKeepEditing={(): void => setShowEditConfirm(false)}
          onDiscard={(): void => {
            if (!chartToEdit) return
            confirmEditChart(chartToEdit)
          }}
        />
      ) : (
        <ChartingEditChartModal
          show={showEditConfirm}
          chartName={chartToEdit?.title ?? ''}
          actionVerb="edit"
          onCancel={(): void => setShowEditConfirm(false)}
          onContinue={(): void => {
            if (!chartToEdit) return
            confirmEditChart(chartToEdit)
          }}
        />
      )}
    </>
  )
}

export default PerioHistory
